import { useTranslation } from 'react-i18next';
import Days from '../../utils/Days.js';
import { daysSince } from '../../utils/Utils.js';
import { formatDate } from '../../../utils/dateFormat.js';
import Patch from '../../utils/Patch.js';

// Calculate the date for a given day offset from start_date
const calculateDate = (startDate, dayOffset) => {
  const date = new Date(startDate + "T12:00:00");
  date.setDate(date.getDate() + dayOffset);
  return formatDate(date);
};

// stepStatus is one protocol step's state for the animal: 'completed',
// 'due-today', 'missed' or 'pending'. Shared by the circles below and the
// mobile card's step list (ReproductionCardList.js).
export const stepStatus = (protocolDay, completed, currentDay) => {
  if (completed) return 'completed';
  // System-action steps run automatically on their exact day — there's
  // nothing for the rancher to prep for, so no day-before early warning.
  // Without this, two system-action steps landing on consecutive days
  // (e.g. mark_lost_pregnancies@314, mark_lot_archived@315) would both
  // show "due today" a day early, which reads as a bug even though the
  // window itself is intentional for actionable steps.
  const dueWindowStart = protocolDay.system_action ? protocolDay.end_day : protocolDay.end_day - 1;
  if (currentDay >= dueWindowStart && currentDay <= protocolDay.end_day) return 'due-today';
  if (currentDay > protocolDay.end_day) return 'missed';
  return 'pending';
};

const STATUS_LABEL_KEY = {
  'completed': 'repro_status_completed',
  'due-today': 'repro_status_due_today',
  'missed':    'repro_status_missed',
  'pending':   'repro_status_pending',
};
export const stepStatusLabelKey = (status) => STATUS_LABEL_KEY[status] || 'repro_status_unknown';

// predictedDate is when the protocol's last step lands (predicted IATF).
export const predictedDate = (protocol, startDate) => {
  if (!protocol?.timeline_days?.length || !startDate) return null;
  const days = Math.max(...protocol.timeline_days.map(d => d.end_day || 0));
  const dt = new Date(startDate + "T10:00:00Z");
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().split('T')[0];
};

// stepDateRange is the step's calendar date, or "start - end" for a
// multi-day step.
export const stepDateRange = (startDate, protocolDay) => {
  const dayDate = calculateDate(startDate, protocolDay.start_day);
  return protocolDay.start_day !== protocolDay.end_day
    ? `${dayDate} - ${calculateDate(startDate, protocolDay.end_day)}`
    : dayDate;
};

export const stepActionLabels = (protocolDay, actions) =>
  (protocolDay.action_ids || [])
    .map(id => (actions || []).find(a => a._id === id)?.label)
    .filter(Boolean);

// saveStepCompleted marks one step done/undone for an animal's lot
// membership. No more not_started->in_progress auto-transition: that value
// doesn't exist in the outcome enum anymore (lots.reproduction_results) —
// the first real outcome recorded is the "in progress" signal now.
export const saveStepCompleted = (animalId, dayIndex, completed, onSaved) => {
  const field = `reproduction.protocol.timeline_days.${dayIndex}.completed`;
  Patch("lots.animals", animalId, field, completed, () => onSaved && onSaved(), () => {});
};

// stepsInDayOrder pairs each step with its ORIGINAL array index, sorted by
// day — the underlying array isn't guaranteed to be sorted (system-action
// steps get appended by migrations rather than inserted in day position),
// and the original index is the field path saveStepCompleted writes to.
export const stepsInDayOrder = (protocol) =>
  protocol.timeline_days
    .map((day, index) => ({ day, index }))
    .sort((a, b) => a.day.start_day - b.day.start_day);

// ReproductionStatus is the single, click-to-toggle view of an animal's
// protocol progress — the colored circles ARE the control now, no separate
// checkbox list or expandable "Protocol View" section. Hover shows the
// resolved action labels bound to that step (action_ids only — legacy
// event/treatments/actions text is no longer read here) plus the step's own
// Note, if any; clicking a circle toggles that step's completed state directly.
// readOnly (the mobile card face) shows progress only: no toggle, no hover
// details — steps are marked done from the card's expanded step list.
// Automatic (system_action) steps are never toggleable: the daily job decides
// whether to run them from the lot's own progress, and marks every animal's
// copy done itself, so a manual toggle only makes the circle disagree with
// what actually ran (jobs/daily_notifications.go in posso-help-api).
export const ReproductionStatus = ({ protocol, animal, animalId, actions, onToggle, readOnly = false }) => {
  const { t } = useTranslation();
  const current_day = daysSince(animal.start_date);

  const DayStatus = ({ protocol_day, animal_status, startDate, dayIndex }) => {
    const hasSystemAction = !!protocol_day.system_action;
    const statusKey = stepStatus(protocol_day, animal_status.completed, current_day);
    const actionLabels = stepActionLabels(protocol_day, actions);

    const handleClick = () => {
      if (readOnly || hasSystemAction || !animalId) return;
      const newCompleted = !animal_status.completed;
      saveStepCompleted(animalId, dayIndex, newCompleted, () => onToggle && onToggle(dayIndex, newCompleted));
    };

    return (
      <div className={`day ${statusKey}${hasSystemAction ? ' automatic' : ''}`} onClick={handleClick}>
        <div className="details">
          <div className="day-status">{t(stepStatusLabelKey(statusKey))}</div>
          <Days
            start={protocol_day.start_day}
            end={protocol_day.end_day}
          />
          <div className="day-date">{stepDateRange(startDate, protocol_day)}</div>
          {hasSystemAction && (
            // Same tooltip text ProtocolForm.js's gold badge shows for this
            // system_action — one shared string reused here instead of
            // duplicating the explanation into every protocol's note.
            <div className="event system-action-event">
              ⚙️ {t(`system_action_${protocol_day.system_action}_tooltip`)}
            </div>
          )}
          {actionLabels.length > 0 && (
            <ul className="event-actions">
              {actionLabels.map((label, i) => <li key={i}>{label}</li>)}
            </ul>
          )}
          {!hasSystemAction && actionLabels.length === 0 && (
            <div className="event">{t('no_actions_for_step')}</div>
          )}
          {protocol_day.note && (
            <div className="day-note">{protocol_day.note}</div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={`status${readOnly ? ' readonly' : ''}`}>
      {stepsInDayOrder(protocol)
        .map(({ day, index }) => {
          const animalStatus = animal?.protocol?.timeline_days?.[index] || { completed: false };
          return (
            <DayStatus
              key={index}
              protocol_day={day}
              animal_status={animalStatus}
              startDate={animal.start_date}
              dayIndex={index}
            />
          );
        })}
    </div>
  );
}
export default ReproductionStatus;
